import { useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, useFocusEffect } from "@react-navigation/native";
import { useColors, ThemeColors } from "../hooks/useColors";
import { spacing, typography } from "../theme";
import { useRealNotifications as useNotifications } from "../hooks/useRealNotifications";
import { NotificationList } from "../components/NotificationList";

type FilterTab = "all" | "messages";

export default function NotificationsScreen() {
  const navigation = useNavigation();
  const colors = useColors();
  const styles = createStyles(colors);
  const [activeFilter, setActiveFilter] = useState<FilterTab>("all");

  const { notifications, unreadCount, markAllAsRead, getNotificationsByType, loading, error } =
    useNotifications();

  // Add console logging for debugging
  console.log('🔔 [NotificationsScreen] Render state:', {
    loading,
    error,
    notificationsCount: notifications?.length || 0,
    unreadCount,
    activeFilter
  });

  // Automatically mark all notifications as read when screen is focused
  useFocusEffect(
    useCallback(() => {
      if (unreadCount > 0) {
        console.log('🔔 [NotificationsScreen] Screen focused, marking all notifications as read');
        markAllAsRead();
      }
    }, [unreadCount, markAllAsRead])
  );

  const getFilteredNotifications = (filter?: FilterTab) => {
    const filterToUse = filter || activeFilter;
    if (notifications === undefined || !Array.isArray(notifications) || notifications.length === 0) return [];

    switch (filterToUse) {
      case "messages":
        return notifications.filter(
          (n) => n?.type === "chat_message"
        );
      default:
        return notifications;
    }
  };

  const getFilterCount = (filter: FilterTab) => {
    const filtered = getFilteredNotifications(filter);
    return filtered.filter((n) => n.status === "unread").length;
  };

  const renderFilterTab = (filter: FilterTab, label: string, icon: string) => {
    const isActive = activeFilter === filter;
    const count = getFilterCount(filter);

    return (
      <TouchableOpacity
        style={[styles.filterTab, isActive && styles.activeFilterTab]}
        onPress={() => {
          setActiveFilter(filter);
        }}
        key={filter}
      >
        <View style={styles.filterTabContent}>
          <Ionicons
            name={icon as any}
            size={18}
            color={isActive ? "#000000" : "#FFFFFF"}
          />
          <Text
            style={[styles.filterLabel, isActive && styles.activeFilterLabel]}
          >
            {label}
          </Text>
          {count > 0 && (
            <View style={styles.filterBadge}>
              <Text style={styles.filterBadgeText}>
                {count > 99 ? "99+" : count.toString()}
              </Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  return (
    <SafeAreaView style={styles.container} edges={["top"]}>
      {/* Header with Back Button */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
      </View>

      {/* Large Title Section */}
      <View style={styles.welcomeSection}>
        <Text style={styles.headerTitle}>Notifications</Text>
      </View>

      {/* Filter Tabs */}
      <View>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.filterContainer}
          contentContainerStyle={styles.filterScrollContent}
        >
          {renderFilterTab("all", "All", "notifications-outline")}
          {renderFilterTab("messages", "Messages", "chatbubble-outline")}
        </ScrollView>
      </View>

      {/* Notification List */}
      <View style={styles.listContainer}>
        {loading ? (
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading notifications...</Text>
          </View>
        ) : error ? (
          <View style={styles.errorContainer}>
            <Text style={styles.errorText}>Error loading notifications: {error}</Text>
          </View>
        ) : (
          <>
            <NotificationList
              filterType={undefined}
              customFilter={
                activeFilter === "all"
                  ? undefined
                  : activeFilter === "messages"
                  ? (notifications) => (notifications || []).filter(n =>
                    n.type === "chat_message"
                  )
                  : undefined
              }
              showHeader={false}
            />
          </>
        )}
      </View>
    </SafeAreaView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: colors.background,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.sm,
      backgroundColor: colors.background,
    },
    backButton: {
      padding: spacing.xs,
      marginLeft: -spacing.xs,
    },
    welcomeSection: {
      paddingLeft: spacing.lg + 4,
      paddingRight: spacing.lg,
      paddingTop: spacing.xs,
      paddingBottom: spacing.sm,
    },
    headerTitle: {
      fontSize: 28,
      fontWeight: "800",
      color: colors.text,
      letterSpacing: -0.5,
    },
    filterContainer: {
      paddingVertical: spacing.sm,
      backgroundColor: colors.background,
    },
    filterScrollContent: {
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.sm,
      gap: 10,
    },
    filterTab: {
      flexDirection: "row",
      alignItems: "center",
      paddingHorizontal: 18,
      paddingVertical: 10,
      borderRadius: 16,
      backgroundColor: colors.surface,
      borderWidth: 1,
      borderColor: colors.border,
      shadowColor: "#000",
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.05,
      shadowRadius: 4,
      elevation: 2,
    },
    activeFilterTab: {
      backgroundColor: colors.primary,
      borderColor: colors.primary,
    },
    filterTabContent: {
      flexDirection: "row",
      alignItems: "center",
    },
    filterLabel: {
      fontSize: 14,
      fontWeight: "500",
      color: "#FFFFFF",
      marginLeft: 8,
    },
    activeFilterLabel: {
      color: "#000000",
    },
    filterBadge: {
      backgroundColor: "#000000",
      borderRadius: 10,
      minWidth: 18,
      height: 18,
      justifyContent: "center",
      alignItems: "center",
      paddingHorizontal: 4,
      marginLeft: 6,
    },
    filterBadgeText: {
      color: colors.white,
      fontSize: 10,
      fontWeight: "bold",
    },
    listContainer: {
      flex: 1,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
    },
    loadingText: {
      ...typography.body,
      color: colors.text,
      fontSize: 16,
    },
    errorContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      backgroundColor: colors.background,
      padding: spacing.lg,
    },
    errorText: {
      ...typography.body,
      color: colors.error,
      fontSize: 16,
      textAlign: 'center',
    },
  });
